import React, {ChangeEvent, useEffect, useMemo, useState} from 'react';

import {format} from 'date-fns';
import {useUnit} from 'effector-react';
import cloneDeep from 'lodash.clonedeep';
import {useTranslation} from 'react-i18next';
import {useLocation} from 'react-router-dom';

import {MonthPeriod} from '@/widgets/MonthPeriod';
import {PageHeader} from '@/widgets/PageHeader';
import {showRestError} from '@app/errors';
import {dateModel} from '@entities/date/model';
import {branchFetcher} from '@entities/relations/api';
import {BranchBuilder} from '@entities/relations/model';
import {RelationsFilters, TaxpayerNode, TaxpayerRelationshipType} from '@entities/relations/types';
import {Button, ButtonVariant} from '@shared/ui/Button';
import {HtmlText} from '@shared/ui/HTMLText';
import {Icon} from '@shared/ui/Icon';
import {Input} from '@shared/ui/Input';
import {Page} from '@shared/ui/Page';

import {
    ActionGroup,
    Card,
    ChartCard,
    ChartContainer,
    ControlsRow,
    ExportButton,
    FilterButton,
    FiltersRow,
    Helper,
    HelperArrow,
    InputBlock,
    InputRow,
    OptionButton,
    PageWrapper,
} from './styled';
import {RelationsChart} from './ui/RelationsChart';
import {RelationsLegend} from './ui/RelationsLegend';

const DEFAULT_FILTERS: RelationsFilters = {
    sum: false,
    sur: false,
    gap: false,
};

const getPeriodFromDate = (date: Date | null) => Number(format(date ?? new Date(), 'yyyyMM'));

export const RelationsPage = () => {
    const {t} = useTranslation();
    const location = useLocation();

    const [tin, setTin] = useState('');
    const [tree, setTree] = useState<TaxpayerNode>();
    const [relType, setRelType] = useState<TaxpayerRelationshipType>('BUY');
    const [filters, setFilters] = useState<RelationsFilters>(DEFAULT_FILTERS);
    const [isLoading, setIsLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [treeType, setTreeType] = useState<TaxpayerRelationshipType>('BUY');
    const date = useUnit(dateModel);

    const period = useMemo(() => getPeriodFromDate(date.state.range.from), [date.state.range.from]);

    const fetchTree = React.useCallback(
        async (nextTin = tin, nextRelType = relType, nextFilters = filters) => {
            if (!nextTin) {
                setTree(undefined);
                return;
            }

            setIsLoading(true);
            try {
                const branch = await branchFetcher.getBranch({
                    type: nextRelType,
                    tin: nextTin,
                    period,
                    size: 6,
                    filters: nextFilters,
                });
                setTree(branch?.build());
                setTreeType(nextRelType);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error);
            } finally {
                setIsLoading(false);
            }
        },
        [filters, period, relType, tin],
    );

    useEffect(() => {
        branchFetcher.connect();
        return () => {
            branchFetcher.close();
        };
    }, []);

    useEffect(() => {
        const queryTin = new URLSearchParams(location.search).get('tin');
        if (queryTin) {
            setTin(queryTin);
            fetchTree(queryTin);
        }
    }, [location.search]);

    useEffect(() => {
        if (tin) fetchTree();
    }, [period]);

    const toggleFilter = (key: keyof RelationsFilters) => {
        setFilters((prev) => ({...prev, [key]: !prev[key]}));
    };

    const handleTinChange = (e: ChangeEvent<HTMLInputElement>) => {
        const nextTin = e.target.value;
        setTin(nextTin);
        if (!nextTin) setTree(undefined);
    };

    const handleTinClear = () => {
        setTin('');
        setTree(undefined);
    };

    const handleRelationTypeChange = (value: TaxpayerRelationshipType) => {
        setRelType(value);
        if (tin) fetchTree(tin, value, filters);
    };

    const handleResetFilters = () => {
        setFilters(DEFAULT_FILTERS);
        if (tin) fetchTree(tin, relType, DEFAULT_FILTERS);
    };

    const handleNodeToggle = async (node: any) => {
        await BranchBuilder.addBranchTo({
            node,
            type: relType,
            filters,
        });
    };

    const handleExport = async () => {
        if (!tree || isExporting || isLoading) return;
        const snapshot = cloneDeep(tree);
        setIsExporting(true);
        try {
            const {downloadVatTree} = await import('@entities/relations/export');
            await downloadVatTree(snapshot, treeType);
        } catch (error) {
            showRestError(error);
        } finally {
            setIsExporting(false);
        }
    };

    const tinAdornment = tin ? (
        <Icon name="cross" width={12} height={12} onClick={handleTinClear} />
    ) : null;

    return (
        <Page title={t('page_relations.title')}>
            <PageWrapper>
                <PageHeader title={t('page_relations.title')}>
                    <MonthPeriod />
                </PageHeader>

                <Card>
                    <ControlsRow>
                        <ActionGroup>
                            <OptionButton
                                type="button"
                                isActive={relType === 'BUY'}
                                onClick={() => handleRelationTypeChange('BUY')}
                            >
                                {t('page_relations.rel_type.buy')}
                            </OptionButton>
                            <OptionButton
                                type="button"
                                isActive={relType === 'SALE'}
                                onClick={() => handleRelationTypeChange('SALE')}
                            >
                                {t('page_relations.rel_type.sale')}
                            </OptionButton>
                        </ActionGroup>

                        <InputBlock>
                            <InputRow>
                                <Input
                                    size="sm"
                                    value={tin}
                                    onChange={handleTinChange}
                                    placeholder={t('page_relations.search')}
                                    adornment={tinAdornment}
                                />
                                <div style={{width: 140}}>
                                    <Button
                                        size="sm"
                                        onClick={() => fetchTree()}
                                        disabled={!tin}
                                        isLoading={isLoading}
                                    >
                                        {t('actions.apply')}
                                    </Button>
                                </div>
                            </InputRow>

                            {!tin && (
                                <Helper>
                                    <HelperArrow />
                                    <HtmlText __html={t('page_relations.search_hint')} />
                                </Helper>
                            )}
                        </InputBlock>
                    </ControlsRow>
                </Card>

                <Card>
                    <FiltersRow>
                        <ActionGroup>
                            <FilterButton
                                type="button"
                                isActive={filters.sum}
                                onClick={() => toggleFilter('sum')}
                            >
                                {t('page_relations.filters.sum')}
                            </FilterButton>
                            <FilterButton
                                type="button"
                                isActive={filters.sur}
                                onClick={() => toggleFilter('sur')}
                            >
                                {t('page_relations.filters.sur')}
                            </FilterButton>
                            <FilterButton
                                type="button"
                                isActive={filters.gap}
                                onClick={() => toggleFilter('gap')}
                            >
                                {t('page_relations.filters.gap_source')}
                            </FilterButton>
                        </ActionGroup>

                        <ActionGroup style={{flexWrap: 'nowrap'}}>
                            <div style={{width: 140}}>
                                <Button
                                    size="sm"
                                    onClick={() => fetchTree()}
                                    disabled={!tin}
                                    isLoading={isLoading}
                                >
                                    {t('actions.apply')}
                                </Button>
                            </div>
                            <div style={{width: 140}}>
                                <Button
                                    size="sm"
                                    variant={ButtonVariant.Outline}
                                    onClick={handleResetFilters}
                                >
                                    {t('actions.cancel')}
                                </Button>
                            </div>
                            <div style={{width: 140}}>
                                <ExportButton
                                    size="sm"
                                    variant={ButtonVariant.Text}
                                    onClick={handleExport}
                                    disabled={!tree || isLoading || isExporting}
                                    isLoading={isExporting}
                                >
                                    {t('page_relations.file.export')}
                                </ExportButton>
                            </div>
                        </ActionGroup>
                    </FiltersRow>
                </Card>

                <ChartCard>
                    <RelationsLegend />
                    <ChartContainer>
                        <RelationsChart
                            data={tree}
                            cardDirection={relType}
                            onNodeToggle={handleNodeToggle}
                        />
                    </ChartContainer>
                </ChartCard>
            </PageWrapper>
        </Page>
    );
};
